import { supabase } from "@/integrations/supabase/client";

interface SendArgs {
  templateName: string;
  recipientEmail: string;
  idempotencyKey: string;
  templateData?: Record<string, unknown>;
}

/**
 * Invoke the transactional email Edge Function.
 *
 * Idempotency is handled **server-side** inside the Edge Function:
 *  - The function checks `email_send_log` for a prior row with the same key.
 *  - It reserves a "pending" row before sending (unique index prevents races).
 *  - After a successful send, it updates the row to "sent".
 *
 * The client no longer needs INSERT access to `email_send_log`.
 */
export async function sendTransactionalEmail({
  templateName,
  recipientEmail,
  idempotencyKey,
  templateData,
}: SendArgs): Promise<{ ok: boolean; error?: string; deduped?: boolean }> {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke(
      "send-transactional-email",
      {
        body: { templateName, recipientEmail, idempotencyKey, templateData },
      },
    );
    if (invokeError) {
      return { ok: false, error: invokeError.message };
    }
    const result = data as { messageId?: string; deduped?: boolean } | null;
    return { ok: true, deduped: result?.deduped ?? false };
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
